<template>
  <div>
    <h5 class="title is-5">
      {{ activeWorkspaceId && !isFormSubmissionInProgress ? 'Looks like you already have a workspace' : 'Create Your Workspace' }}
    </h5>
    <p class="is-size-6 u-m-b">
      <strong>A StandupPro Workspace</strong> can host multiple teams
      and an unlimited number of team members.
      For most people it makes sense to use their <strong>company name</strong>
      as their workspace.
    </p>
    <form @submit.prevent="handleSubmitCreateWorkspace">
      <b-field
        label="Workspace Name"
        :type="formErrors[createWorkspaceForm.workspaceName.name] ? 'is-danger' : ''"
        :message="formErrors[createWorkspaceForm.workspaceName.name] || ''"
      >
        <b-input
          v-model="createWorkspaceForm.workspaceName.value"
          placeholder="Ex: Vandelay Industries"
          :disabled="activeWorkspaceId || isFormSubmissionInProgress"
          ref="workspaceNameInput"
        />
      </b-field>

      <b-button
        type="is-primary"
        native-type="submit"
        size="is-large"
        class="is-fullwidth"
        :disabled="isFormSubmissionInProgress"
      >
        <span v-if="isFormSubmissionInProgress">
          {{ createWorkspaceProgressText }}
        </span>
        <span v-else>
          {{ activeWorkspaceId ? 'Next Step' : 'Create My Workspace' }}
        </span>
      </b-button>

      <b-progress
        type="is-primary"
        class="u-m-t onboarding-create-workspace-form"
        :style="{'visibility': isFormSubmissionInProgress ? 'visible' : 'hidden'}"
        :value="createWorkspaceProgressValue"
      />

    </form>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import * as WorkspaceActions from '@/store/actions/Workspace.actions';
import * as SubscriptionActions from '@/store/actions/Subscription.actions';
import * as ValidationService from '@/services/Validation.service';
import { isObjectEmpty } from '@/helpers/dataHelpers';
import * as ApiService from '@/services/Api.service';
import * as SubscriptionTypes from '@/constants/Subscription.types';

export default {
  name: 'OnboardingCreateWorkspaceForm',

  data() {
    return {
      createWorkspaceForm: {
        workspaceName: {
          name: 'Workspace Name',
          value: '',
          required: true,
        },
      },
      createWorkspaceError: null,
      isFormSubmissionInProgress: false,
      formErrors: {},
      createWorkspaceProgressText: 'Working . . .',
      createWorkspaceProgressValue: 0,
    };
  },

  computed: {
    ...mapGetters([
      'loggedInUser',
      'activeWorkspace',
    ]),
    ...mapState({
      activeWorkspaceId: state => state.workspace.activeWorkspaceId,
      loggedInUserId: state => state.auth.loggedInUserId,
    }),
  },

  mounted() {
    this.setInitialFormState();
  },

  methods: {
    ...mapActions({
      createWorkspace: WorkspaceActions.CREATE_WORKSPACE,
      createSubscription: SubscriptionActions.CREATE_SUBSCRIPTION,
    }),
    setInitialFormState() {
      this.createWorkspaceForm.workspaceName.value = this.activeWorkspace ? this.activeWorkspace.workspaceName : '';

      this.$refs.workspaceNameInput.$el.querySelector('input').focus();
    },
    async handleSubmitCreateWorkspace() {
      this.formErrors = ValidationService.runFormValidation({ ...this.createWorkspaceForm });
      if (!isObjectEmpty(this.formErrors)) return;

      this.isFormSubmissionInProgress = true;
      if (this.activeWorkspaceId) {
        this.$emit('submitSuccess');
        this.isFormSubmissionInProgress = false;
      } else {
        try {
          this.createWorkspaceProgressText = 'Reticulating Splines';
          this.createWorkspaceProgressValue = 25;

          await this.createWorkspace({
            workspaceName: this.createWorkspaceForm.workspaceName.value,
          });

          const { data: { customer } } = await ApiService.createStripeCustomer({
            email: this.loggedInUser.email,
            name: this.activeWorkspace.workspaceName,
            workspaceId: this.activeWorkspaceId,
          });

          this.createWorkspaceProgressText = 'Building Data Trees';
          this.createWorkspaceProgressValue = 50;

          const { data: { subscription } } = await ApiService.createStripeSubscription({
            customer: customer.id,
            price: SubscriptionTypes.STRIPE_PRICE_TYPE_FREE_FOREVER(this.$env),
          });

          this.createWorkspaceProgressText = 'Jumping to Hyperspace';
          this.createWorkspaceProgressValue = 75;

          await this.createSubscription({
            workspaceId: this.activeWorkspaceId,
            stripePricingPlan: SubscriptionTypes.STRIPE_PRICE_TYPE_FREE_FOREVER(this.$env),
            stripeCustomerId: customer.id,
            stripeSubscriptionId: subscription.id,
          });

          this.createWorkspaceProgressText = 'Finished!';
          this.createWorkspaceProgressValue = 100;

          await new Promise(resolve => setTimeout(resolve, 1000));

          this.$emit('submitSuccess');
          this.isFormSubmissionInProgress = false;
        } catch (err) {
          this.isFormSubmissionInProgress = false;
        }
      }
    },
  },
};
</script>

<!-- Removed scoping to override b-progress styles -->

<style lang="scss">
.onboarding-create-workspace-form {
  .progress {
    border-radius: 4px;
  }
}
</style>
